import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ARBOR_INITIAL_VALUES, Arbor } from "@/pages/catalogs/models/Arbor";
import { upsertArbor } from "@/pages/catalogs/repositories/ArborRepository";
import { Stand } from "@/pages/configuration/models/Stand";
import { StandGrid } from "@/pages/configuration/pages/parts/StandGrid";
import { setStandsToPart } from "@/pages/configuration/repositories/StandRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { LocationTreeSelector } from "./LocationTreeSelector";

type Props = {
	show: boolean;
	onHide: (partId?: number, partName?: string) => void;
};

export const NewArborDialog = ({ show, onHide }: Props) => {
	const queryClient = useQueryClient();

	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [standsSelected, setStandsSelected] = useState<Stand[]>([]);

	const { control, reset, getValues, trigger, watch } = useForm<Arbor>({
		mode: "onChange",
		defaultValues: ARBOR_INITIAL_VALUES,
	});

	const arborName = watch("PartName");

	const createArbor = useMutation({
		mutationFn: upsertArbor,
		onSuccess: async (partId) => {
			if (partId) {
				const resp = await setStandsToPart(partId, standsSelected);
				if (!resp.ok) {
					setError(resp.message);
					return;
				}
			}
			setNotificationMessage("Arbor created successfully");
			await queryClient.invalidateQueries({ queryKey: ["arbors"] });
			onHide(partId, arborName);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const handleSave = async () => {
		const isValid = await trigger();
		if (!isValid) return;

		setIsSubmitLoading(true);
		const data = getValues();
		createArbor.mutate({ ...data, PartId: null });
	};

	const handleCancel = () => {
		reset(ARBOR_INITIAL_VALUES);
		setStandsSelected([]);
		onHide();
	};

	const handleClose = () => {
		reset(ARBOR_INITIAL_VALUES);
		setStandsSelected([]);
		onHide();
	};

	return (
		<>
			<MesfModal
				open={show}
				handleClose={handleClose}
				title="NEW ARBOR"
				maxWidth="md"
				id="new-arbor-modal"
			>
				<MesfModal.Content
					dividers
					sx={{
						padding: (theme) => theme.spacing(2),
						minWidth: 500,
						overflowY: "auto",
					}}
				>
					<Grid2 container spacing={1}>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="PartName"
								control={control}
								rules={{ required: "Name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										required
										label="Name"
										variant="outlined"
										{...field}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: { "data-test-id": `arbor-name-input` },
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="LocationId"
								control={control}
								render={({ field }) => (
									<LocationTreeSelector
										label="Location"
										value={field.value}
										onChange={field.onChange}
									/>
								)}
							/>
						</Grid2>
						<StandGrid
							onSelectionChanged={setStandsSelected}
							standsOfPart={standsSelected}
						/>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "15px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="inherit"
								onClick={handleCancel}
								disabled={isSubmitLoading}
								fullWidth
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								startIcon={
									isSubmitLoading ? (
										<CircularProgress size="1rem" color="inherit" />
									) : null
								}
								disabled={isSubmitLoading}
								fullWidth
								data-test-id="create-arbor-button"
							>
								Create
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Creating Arbor"
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
